import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "./auth-context";
import { GRID_PREFS_PREFIX as STORAGE_PREFIX } from "./client-storage";

function storageKey(userId: string, pageKey: string): string {
  return `${STORAGE_PREFIX}${userId}:${pageKey}`;
}

/**
 * Persists list page URL search params (filters, sort, page) to localStorage.
 * On mount, restores saved params if the URL has none.
 */
export function useGridPreferences(pageKey: string): void {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const restored = useRef(false);
  const userId = user?.user.id;

  // Restore saved params once when user is available and URL is bare
  useEffect(() => {
    if (restored.current || !userId) return;
    restored.current = true;

    if (searchParams.toString()) return; // URL already has params -- honour them

    const stored = localStorage.getItem(storageKey(userId, pageKey));
    if (stored) {
      setSearchParams(new URLSearchParams(stored), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Save params whenever they change. 207 -- never persist the free-text search
  // param (`q`): it can contain a searched pupil's name and must not be written
  // to disk, where it would survive logout on a shared machine.
  useEffect(() => {
    if (!userId) return;
    const persisted = new URLSearchParams(searchParams);
    persisted.delete("q");
    localStorage.setItem(storageKey(userId, pageKey), persisted.toString());
  }, [searchParams, userId, pageKey]);
}

/**
 * Returns the URL for a list page including stored grid preferences.
 * @param basePath - The URL path to navigate to (e.g. "/schools")
 * @param userId - Current user ID for localStorage key
 * @param pageKey - Optional override for the storage key (defaults to basePath minus leading slash).
 *                  Use this for school-scoped pages where the path is dynamic but the
 *                  preferences key should be stable (e.g. "events" for /schools/:id/events).
 */
export function getListUrl(basePath: string, userId: string | undefined, pageKey?: string): string {
  if (!userId) return basePath;
  const key = pageKey ?? basePath.replace(/^\//, "");
  const stored = localStorage.getItem(storageKey(userId, key));
  return stored ? `${basePath}?${stored}` : basePath;
}
